import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const EventFeed = () => {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState('all');

  const mockEvents = [
    {
      id: 1,
      type: 'transaction',
      severity: 'info',
      title: 'High Volume Transaction',
      description: 'BTC swap processed: $45,230 → USDC',
      system: 'Swap Engine',
      timestamp: new Date(Date.now() - 30000),
      icon: 'ArrowRightLeft'
    },
    {
      id: 2,
      type: 'alert',
      severity: 'warning',
      title: 'API Response Time Elevated',
      description: 'Wallet connectivity API: 450ms (threshold: 300ms)',
      system: 'API Gateway',
      timestamp: new Date(Date.now() - 120000),
      icon: 'AlertTriangle'
    },
    {
      id: 3,
      type: 'success',
      severity: 'success',
      title: 'Swap Optimization Applied',
      description: 'Route efficiency improved by 12% for ETH→USDT',
      system: 'Routing Engine',
      timestamp: new Date(Date.now() - 180000),
      icon: 'CheckCircle'
    },
    {
      id: 4,
      type: 'error',
      severity: 'error',
      title: 'Card Tokenization Failed',
      description: '3 consecutive failures for merchant ID: MCH-8472',
      system: 'Card Rail',
      timestamp: new Date(Date.now() - 240000),
      icon: 'XCircle'
    },
    {
      id: 5,
      type: 'transaction',
      severity: 'info',
      title: 'Wallet Connection Established',
      description: 'MetaMask integration: User ID USR-9234',
      system: 'Wallet Service',
      timestamp: new Date(Date.now() - 300000),
      icon: 'Wallet'
    },
    {
      id: 6,
      type: 'alert',
      severity: 'warning',
      title: 'Liquidity Threshold Alert',
      description: 'USDC balance below 50k threshold: 48,234 USDC',
      system: 'Liquidity Monitor',
      timestamp: new Date(Date.now() - 360000),
      icon: 'TrendingDown'
    },
    {
      id: 7,
      type: 'success',
      severity: 'success',
      title: 'Auto-Conversion Triggered',
      description: 'BTC → USDC conversion: 0.5 BTC at $42,150',
      system: 'Automation Engine',
      timestamp: new Date(Date.now() - 420000),
      icon: 'Zap'
    },
    {
      id: 8,
      type: 'transaction',
      severity: 'info',
      title: 'Merchant Settlement Processed',
      description: 'Daily settlement: $125,430 to MCH-5621',
      system: 'Settlement Service',
      timestamp: new Date(Date.now() - 480000),
      icon: 'DollarSign'
    }
  ];

  useEffect(() => {
    setEvents(mockEvents);

    const interval = setInterval(() => {
      const newEvent = {
        ...mockEvents?.[Math.floor(Math.random() * mockEvents?.length)],
        id: Date.now(),
        timestamp: new Date()
      };
      setEvents(prev => [newEvent, ...prev?.slice(0, 19)]);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'error':
        return 'border-error/30 bg-error/5 text-error';
      case 'warning':
        return 'border-warning/30 bg-warning/5 text-warning';
      case 'success':
        return 'border-success/30 bg-success/5 text-success';
      default:
        return 'border-border bg-card text-foreground';
    }
  };

  const getTimeAgo = (timestamp) => {
    const seconds = Math.floor((new Date() - timestamp) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  };

  const filteredEvents = filter === 'all' 
    ? events 
    : events?.filter(e => e?.severity === filter);

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <h2 className="text-base md:text-lg font-semibold text-foreground">Real-Time Events</h2>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
          <span className="text-xs md:text-sm text-muted-foreground caption">Live Feed</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {['all', 'error', 'warning', 'success', 'info']?.map((filterType) => (
          <button
            key={filterType}
            onClick={() => setFilter(filterType)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-250 ${
              filter === filterType
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted/30 text-muted-foreground hover:bg-muted/50'
            }`}
          >
            {filterType?.charAt(0)?.toUpperCase() + filterType?.slice(1)}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-thin space-y-2 md:space-y-3">
        {filteredEvents?.map((event) => (
          <div
            key={event?.id}
            className={`rounded-lg border p-3 md:p-4 transition-all duration-250 hover:shadow-md cursor-pointer ${getSeverityColor(event?.severity)}`}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <Icon name={event?.icon} size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="text-sm font-semibold text-foreground line-clamp-1">
                    {event?.title}
                  </h3>
                  <span className="text-xs text-muted-foreground caption whitespace-nowrap">
                    {getTimeAgo(event?.timestamp)}
                  </span>
                </div>
                <p className="text-xs md:text-sm text-muted-foreground mb-2 line-clamp-2">
                  {event?.description}
                </p>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-muted/50 text-xs caption">
                    <Icon name="Server" size={12} />
                    {event?.system}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-border">
        <button className="w-full py-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors duration-250">
          View All Events
        </button>
      </div>
    </div>
  );
};

export default EventFeed;